/**
 * Validates a service base URL extracted from a U2C catalog response before it
 * is used as the target of a credential-bearing request. The U2C catalog body
 * is not part of the TLS-pinned request and could be poisoned, so the host must
 * be confirmed to belong to a known Cisco/Webex domain (and use HTTPS) before
 * any token is sent to it.
 */
const ALLOWED_U2C_HOST_SUFFIXES: ReadonlyArray<string> = [
  '.wbx2.com',
  '.ciscospark.com',
  '.webex.com',
  '.webexgov.us',
  '.webexapis.com',
];

/**
 * Returns the input URL when its host matches the Cisco/Webex allowlist and it
 * uses HTTPS; throws otherwise. Callers should treat a throw as "do not send
 * credentials to this URL".
 *
 * @param baseUrl the base URL taken from a U2C catalog response
 */
export function validateCatalogUrl(baseUrl: string): string {
  let parsed: URL;
  try {
    parsed = new URL(baseUrl);
  } catch {
    throw new Error('U2C service URL is not a valid URL');
  }
  if (parsed.protocol !== 'https:') {
    throw new Error('U2C service URL must use https');
  }
  const host = parsed.hostname.toLowerCase();
  const allowed = ALLOWED_U2C_HOST_SUFFIXES.some(
    (suffix) => host === suffix.slice(1) || host.endsWith(suffix)
  );
  if (!allowed) {
    throw new Error('U2C service URL host is not in the allowed Cisco/Webex domain set');
  }
  return baseUrl;
}
